import { Calculator, Target, Tag, AlertCircle, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ConfigurationTypeEnum,
  Configuration,
  ContainerMaterialMatrixConfiguration,
  ContainerPeriodPricingConfiguration,
  PayAsYouGoPricingConfiguration,
  TonnagePricingConfiguration,
  AutoDiscountConfiguration,
  DiscountConfiguration,
  DiscountTypeEnum,
} from '@libs/schemas';
import {
  ContainerMaterialMatrix,
  MatrixData,
  ApiMaterial,
} from 'components/pricing/components/ContainerMaterialMatrix';
import {
  ContainerPeriodMatrix,
  PeriodPricingTier,
} from 'components/pricing/components/ContainerPeriodMatrix';
import {
  ContainerPayAsYouGoMatrix,
  PayAsYouGoContainerRate,
} from 'components/pricing/components/ContainerPayAsYouGoMatrix';
import { DiscountConfigurationForm } from 'components/pricing/components/DiscountConfigurationForm';
import type { ContainerType } from 'src/trpc';
import { NumericFormat } from 'react-number-format';
import { ConfigurationFieldValue } from 'components/pricing/PricingRuleSheet';

interface ErrorRecord {
  [key: string]: string;
}

interface ConfigurationStepProps {
  configuration: Configuration;
  errors: ErrorRecord;
  containers: ContainerType[];
  materialsData: ApiMaterial[];
  onConfigurationChange: (field: string, value: ConfigurationFieldValue) => void;
  onMatrixDataChange: (data: MatrixData) => void;
  onContainerPeriodsChange: (data: Record<string, PeriodPricingTier>) => void;
  onContainerRatesChange: (data: Record<string, PayAsYouGoContainerRate>) => void;
}

export function ConfigurationStep({
  configuration,
  errors,
  containers,
  materialsData,
  onConfigurationChange,
  onMatrixDataChange,
  onContainerPeriodsChange,
  onContainerRatesChange,
}: ConfigurationStepProps) {
  const getConfigIcon = () => {
    switch (configuration.type) {
      case ConfigurationTypeEnum.TONNAGE_PRICING:
        return <Calculator className="h-5 w-5 text-gray-600" />;
      case ConfigurationTypeEnum.AUTO_DISCOUNT:
        return <Target className="h-5 w-5 text-gray-600" />;
      case ConfigurationTypeEnum.DISCOUNT:
        return <Tag className="h-5 w-5 text-gray-600" />;
      case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX:
        return <Calculator className="h-5 w-5 text-gray-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getConfigTitle = () => {
    switch (configuration.type) {
      case ConfigurationTypeEnum.TONNAGE_PRICING:
        return 'Tonnage Pricing Configuration';
      case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING:
        return 'Flat Rate Configuration';
      case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING:
        return 'Pay As You Go Configuration';
      case ConfigurationTypeEnum.AUTO_DISCOUNT:
        return 'Automatic Discount Configuration';
      case ConfigurationTypeEnum.DISCOUNT:
        return 'Discount Configuration';
      case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX:
        return 'Single Material Configuration';
      default:
        return 'Configuration';
    }
  };

  const renderConfigurationForm = () => {
    switch (configuration.type) {
      case ConfigurationTypeEnum.TONNAGE_PRICING: {
        const tonnageConfig = configuration as TonnagePricingConfiguration;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                {getConfigIcon()}
                <CardTitle className="text-md">{getConfigTitle()}</CardTitle>
              </div>
              <CardDescription>
                Set the per-ton rate and minimum tonnage for orders. This applies when no specific
                container-material rules match.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="perTonRate" className="text-sm font-medium">
                    Rate per Ton
                  </Label>
                  <div className="relative mt-1.5">
                    <NumericFormat
                      customInput={Input}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      id="perTonRate"
                      type="text"
                      step="0.01"
                      placeholder="150.00"
                      value={
                        tonnageConfig.data.perTonRateInCents
                          ? (tonnageConfig.data.perTonRateInCents / 100).toFixed(2)
                          : ''
                      }
                      onValueChange={(values) =>
                        onConfigurationChange(
                          'data.perTonRateInCents',
                          Math.round((values.floatValue || 0) * 100),
                        )
                      }
                      className="w-full"
                    />
                  </div>
                  {errors.perTonRate && (
                    <p className="text-sm text-destructive mt-1.5 dark:text-destructive">
                      {errors.perTonRate}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    This rate will be multiplied by the tonnage of each order. For example, a 2-ton
                    order at $150/ton = $300.
                  </p>
                </div>

                <div>
                  <Label htmlFor="minimumTonnage" className="text-sm font-medium">
                    Minimum Tonnage
                  </Label>
                  <div className="relative mt-1.5">
                    <NumericFormat
                      customInput={Input}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      id="minimumTonnage"
                      type="text"
                      step="0.1"
                      placeholder="1.0"
                      value={tonnageConfig.data.minimumTonnage || ''}
                      onValueChange={(values) =>
                        onConfigurationChange('data.minimumTonnage', values.floatValue || 0)
                      }
                      className="w-full"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      tons
                    </span>
                  </div>
                  {errors.minimumTonnage && (
                    <p className="text-sm text-destructive mt-1.5 dark:text-destructive">
                      {errors.minimumTonnage}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Orders below this tonnage will be charged for the minimum tonnage amount.
                  </p>
                </div>

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Example:</strong> With $120/ton and 1.5 ton minimum, a 1-ton order would
                    be charged for 1.5 tons = $180.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        );
      }

      case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX: {
        const matrixConfig = configuration as ContainerMaterialMatrixConfiguration;
        return (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Set a fixed price for each container + material combination. Includes fixed price,
                included days, and daily overage fee.
              </p>
            </div>

            <ContainerMaterialMatrix
              matrixData={matrixConfig.data.matrixData || {}}
              onMatrixChange={(data) => {
                onMatrixDataChange(data);
              }}
              containers={containers}
              materialsData={materialsData}
            />

            {errors.matrix && (
              <p className="text-sm text-destructive mt-1 dark:text-destructive">{errors.matrix}</p>
            )}
          </div>
        );
      }

      case ConfigurationTypeEnum.AUTO_DISCOUNT: {
        const discountConfig = configuration as AutoDiscountConfiguration;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                {getConfigIcon()}
                <CardTitle className="text-md">{getConfigTitle()}</CardTitle>
              </div>
              <CardDescription className="text-sm text-muted mt-1">
                Automatically apply discounts when certain conditions are met.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <DiscountConfigurationForm
                discountData={discountConfig.data}
                errors={errors}
                isAutomatic={true}
                onConfigurationChange={onConfigurationChange}
              />
            </CardContent>
          </Card>
        );
      }

      case ConfigurationTypeEnum.DISCOUNT: {
        const discountConfig = configuration as DiscountConfiguration;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                {getConfigIcon()}
                <CardTitle className="text-md">{getConfigTitle()}</CardTitle>
              </div>
              <CardDescription className="text-sm text-muted mt-1">
                Create a discount rule. Note: This discount type requires manual application and
                will not be applied automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <DiscountConfigurationForm
                discountData={discountConfig.data}
                errors={errors}
                isAutomatic={false}
                onConfigurationChange={onConfigurationChange}
              />
            </CardContent>
          </Card>
        );
      }

      case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
        const periodConfig = configuration as ContainerPeriodPricingConfiguration;
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Flat Rate Configuration</h4>
              <p className="text-sm text-blue-700">
                Configure fixed rate packages for containers with base prices, included
                tonnage/days, and overage rates. Example: $295 for 10 days (2.5 tons included) +
                $15/extra day + $75/extra ton.
              </p>
            </div>

            <ContainerPeriodMatrix
              containerPeriods={periodConfig.data.containerPeriods || {}}
              onContainerPeriodsChange={onContainerPeriodsChange}
              containers={containers}
            />

            {errors.periodMatrix && (
              <p className="text-sm text-destructive mt-1 dark:text-destructive">
                {errors.periodMatrix}
              </p>
            )}
          </div>
        );
      }

      case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING: {
        const payAsYouGoConfig = configuration as PayAsYouGoPricingConfiguration;
        return (
          <div className="space-y-4">
            <ContainerPayAsYouGoMatrix
              containerRates={payAsYouGoConfig.data.containerRates || {}}
              onContainerRatesChange={onContainerRatesChange}
              containers={containers}
            />

            {errors.containerRates && (
              <p className="text-sm text-destructive mt-1 dark:text-destructive">
                {errors.containerRates}
              </p>
            )}
          </div>
        );
      }

      default:
        return (
          <Card className="border-gray-200">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Configuration Required</h3>
                <p className="text-gray-500">
                  Please select a pricing method in the previous step to configure this rule.
                </p>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  const isConfigurationValid = () => {
    if (!configuration || Object.keys(configuration).length === 0) {
      return false;
    }

    switch (configuration.type) {
      case ConfigurationTypeEnum.TONNAGE_PRICING: {
        const tonnageConfig = configuration as TonnagePricingConfiguration;
        return !!(
          tonnageConfig.data.perTonRateInCents &&
          tonnageConfig.data.perTonRateInCents > 0 &&
          tonnageConfig.data.minimumTonnage &&
          tonnageConfig.data.minimumTonnage > 0
        );
      }
      case ConfigurationTypeEnum.AUTO_DISCOUNT:
      case ConfigurationTypeEnum.DISCOUNT: {
        const discountConfig = configuration as AutoDiscountConfiguration;

        if (discountConfig.data.discountType === DiscountTypeEnum.PERCENTAGE) {
          const isValid = !!(
            discountConfig.data.discountPercent !== undefined &&
            discountConfig.data.discountPercent > 0
          );
          return isValid;
        } else {
          const isValid = !!(
            discountConfig.data.discountAmountInCents !== undefined &&
            discountConfig.data.discountAmountInCents > 0
          );
          return isValid;
        }
      }
      case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX:
        return true;
      case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
        const periodConfig = configuration as ContainerPeriodPricingConfiguration;
        return !!(
          periodConfig.data.containerPeriods &&
          Object.keys(periodConfig.data.containerPeriods).length > 0
        );
      }
      case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING: {
        const payAsYouGoConfig = configuration as PayAsYouGoPricingConfiguration;
        return !!(
          payAsYouGoConfig.data.containerRates &&
          Object.keys(payAsYouGoConfig.data.containerRates).length > 0
        );
      }
      default:
        return false;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Configuration Details</h3>
        <p className="text-sm text-gray-600 mt-1">
          Set up the specific parameters for your pricing rule.
        </p>
      </div>

      {renderConfigurationForm()}

      {configuration.type && !isConfigurationValid() && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please complete all required configuration fields to continue.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
